import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import API_URL from '../../config';
import '../../App.css';

const QueryTracker = () => {
  const [queries, setQueries] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQueries();
    fetchReminders();
  }, []);

  const fetchQueries = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/clearinghouse/queries`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setQueries(response.data || []);
    } catch (err) {
      console.error('Error fetching queries:', err);
      setError('Failed to load queries');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/clearinghouse/reminders`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setReminders(response.data || []);
    } catch (err) {
      console.error('Error fetching reminders:', err);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysSince = (date) => {
    if (!date) return null;
    const today = new Date();
    const queryDate = new Date(date);
    return Math.floor((today.getTime() - queryDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getResultBadge = (result) => {
    const badges = {
      clean: { text: 'Clean', color: '#10B981', bg: 'rgba(16, 185, 129, 0.2)' },
      'violations-found': { text: 'Violations Found', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.2)' },
      pending: { text: 'Pending', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.2)' },
    };

    const badge = badges[result] || badges.pending;
    return (
      <span
        className="result-badge"
        style={{
          color: badge.color,
          backgroundColor: badge.bg,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '0.85rem',
          fontWeight: 600,
        }}
      >
        {badge.text}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading queries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Clearinghouse Query Tracker</h1>
        <p className="page-subtitle">
          Track and manage all Clearinghouse queries for your drivers
        </p>
      </div>

      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
        </div>
      )}

      {/* Reminders Section */}
      {reminders.length > 0 && (
        <div className="info-card warning-card" style={{ marginBottom: '30px' }}>
          <h2><AlertTriangle size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} /> Annual Queries Due</h2>
          <p>The following drivers need annual queries (over 365 days since last query):</p>
          <div className="reminders-list">
            {reminders.map((reminder, index) => (
              <div key={index} className="reminder-item">
                <div>
                  <strong>{reminder.driverName}</strong>
                  {reminder.driverCDL && <span style={{ color: '#999', marginLeft: '10px' }}>CDL: {reminder.driverCDL}</span>}
                </div>
                <div style={{ color: '#F59E0B', fontSize: '0.9rem' }}>
                  Last query: {formatDate(reminder.lastQueryDate)} ({reminder.daysOverdue} days overdue)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="page-actions">
        <button
          className="btn btn-primary"
          onClick={() => setShowQueryModal(true)}
        >
          + Log New Query
        </button>
      </div>

      {/* Queries List */}
      {queries.length === 0 ? (
        <div className="info-card">
          <p style={{ textAlign: 'center', color: '#999' }}>
            No queries logged yet. Click "Log New Query" to get started.
          </p>
        </div>
      ) : (
        <div className="queries-list">
          {queries.map((query) => {
            const daysSince = getDaysSince(query.queryDate);
            return (
              <div key={query.id} className="query-card">
                <div className="query-header">
                  <div>
                    <h3 style={{ margin: 0, color: '#fff' }}>{query.queriedDriverName}</h3>
                    {query.queriedDriverCDL && (
                      <p style={{ margin: '5px 0 0 0', color: '#999', fontSize: '0.9rem' }}>
                        CDL: {query.queriedDriverCDL}
                      </p>
                    )}
                  </div>
                  {getResultBadge(query.result)}
                </div>

                <div className="query-details">
                  <div className="detail-row">
                    <span className="detail-label">Query Date:</span>
                    <span className="detail-value">{formatDate(query.queryDate)}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Query Type:</span>
                    <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                      {query.queryType === 'full' ? 'Full (Pre-Employment)' : 'Limited (Annual)'}
                    </span>
                  </div>

                  {daysSince !== null && (
                    <div className="detail-row">
                      <span className="detail-label">Days Since Query:</span>
                      <span className="detail-value">
                        {daysSince} {daysSince === 1 ? 'day' : 'days'}
                        {daysSince > 365 && (
                          <span style={{ color: '#F59E0B', marginLeft: '10px' }}>
                            (Annual query due)
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {query.notes && (
                    <div className="query-notes">
                      <strong>Notes:</strong> {query.notes}
                    </div>
                  )}

                  {query.resultDocumentUrl && (
                    <div className="query-document">
                      <a
                        href={`${API_URL}${query.resultDocumentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary btn-sm"
                      >
                        View Result Document
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showQueryModal && (
        <QueryModal
          isOpen={showQueryModal}
          onClose={() => setShowQueryModal(false)}
          onSuccess={() => {
            fetchQueries();
            fetchReminders();
          }}
        />
      )}
    </div>
  );
};

// Query Modal Component
const QueryModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    queriedDriverName: '',
    queriedDriverCDL: '',
    queryDate: new Date().toISOString().split('T')[0],
    queryType: 'full',
    result: 'pending',
    notes: '',
  });

  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.queriedDriverName) {
      setErrors({ queriedDriverName: 'Driver name is required' });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      if (file) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('queriedDriverName', formData.queriedDriverName);
        uploadFormData.append('queriedDriverCDL', formData.queriedDriverCDL || '');
        uploadFormData.append('queryDate', formData.queryDate);
        uploadFormData.append('queryType', formData.queryType);
        uploadFormData.append('result', formData.result);
        uploadFormData.append('notes', formData.notes || '');

        await axios.post(
          `${API_URL}/api/clearinghouse/queries`,
          uploadFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        await axios.post(
          `${API_URL}/api/clearinghouse/queries`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      onSuccess();
      onClose();
      // Reset form
      setFormData({
        queriedDriverName: '',
        queriedDriverCDL: '',
        queryDate: new Date().toISOString().split('T')[0],
        queryType: 'full',
        result: 'pending',
        notes: '',
      });
      setFile(null);
    } catch (err) {
      console.error('Error creating query:', err);
      alert(
        err.response?.data?.message ||
        'Failed to log query. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vehicle-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Log New Query</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="vehicle-form">
          <div className="form-section">
            <h3 className="form-section-title">Query Information</h3>
            <div className="form-section-content">
              <div className="form-row">
                <div className="input-group">
                  <input
                    type="text"
                    name="queriedDriverName"
                    value={formData.queriedDriverName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.queriedDriverName ? 'error' : ''}`}
                    placeholder="Driver Full Name"
                    required
                  />
                  <label className="form-label">
                    Driver Name <span className="required">*</span>
                  </label>
                  {errors.queriedDriverName && (
                    <span className="error-message">{errors.queriedDriverName}</span>
                  )}
                </div>

                <div className="input-group">
                  <input
                    type="text"
                    name="queriedDriverCDL"
                    value={formData.queriedDriverCDL}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="CDL Number"
                  />
                  <label className="form-label">CDL Number</label>
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <input
                    type="date"
                    name="queryDate"
                    value={formData.queryDate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                  <label className="form-label">Query Date <span className="required">*</span></label>
                </div>

                <div className="input-group">
                  <select
                    name="queryType"
                    value={formData.queryType}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="full">Full (Pre-Employment)</option>
                    <option value="limited">Limited (Annual)</option>
                  </select>
                  <label className="form-label">Query Type <span className="required">*</span></label>
                </div>
              </div>

              <div className="input-group">
                <select
                  name="result"
                  value={formData.result}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="pending">Pending</option>
                  <option value="clean">Clean</option>
                  <option value="violations-found">Violations Found</option>
                </select>
                <label className="form-label">Result <span className="required">*</span></label>
              </div>

              <div className="input-group">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="form-input file-input"
                />
                <label className="form-label">
                  Upload Query Result Document
                  {file && <span className="file-name">: {file.name}</span>}
                </label>
              </div>

              <div className="input-group">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-input textarea"
                  placeholder="Additional notes..."
                  rows={3}
                />
                <label className="form-label">Notes</label>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-button cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-button save"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Log Query'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QueryTracker;


